import {
  configureStore,
  combineReducers,
  CombinedState,
  AnyAction,
} from '@reduxjs/toolkit';
import {
  shallowEqual,
  TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import authSlice from './slices/auth.slice';
import appSlice from './slices/app.slice';
import testSlice from './slices/test.slice';
import { userApi } from './apiSlice/user.slices';
import { SomeApi } from './apiSlice/some.slices';
import { AuthAmplify } from './apiSlice/authAmplify.slice';
import { applicationApi } from './apiSlice/application.slice';
import { visitationApi } from './apiSlice/visitation.slices';
import { animalApi } from './apiSlice/animal.slices';
import { policyApi } from './apiSlice/policy.slice';
import { tenantApi } from './apiSlice/tenant.slices';
import { PropertyManagementApi } from './apiSlice/propertyManagement.slices';
import { filtersSlice } from './slices/filters.slice';
import { fileApi } from './apiSlice/file.slice';
import { storageApi, storageAdminApi } from './apiSlice/storage.slice';
import { DocPacketApi } from './apiSlice/docPacket.slice';
import { generalApi } from './apiSlice/generalApis.slice';
import { commonApi } from './apiSlice/comonApis.slice';
import { faqApi } from './apiSlice/faq.slice';
import { analyticsApi } from './apiSlice/analytics.slice';
import { curriculumApi } from './apiSlice/curriculum.slice';
import { landlordApi } from './apiSlice/landlord.slice';
import { authApi } from './apiSlice/auth.slice';
import { authPasswordApi } from './apiSlice/authPassword.slice';
import { profileApi } from './apiSlice/profile.slice';
import { partnerApiIntegrationAppApi } from './apiSlice/partnerApiIntegrationApp.slice';

// template-flag-0

type TState =
  | CombinedState<{
      [x: string]: unknown;
    }>
  | undefined;

const appReducer = combineReducers({
  [SomeApi.reducerPath]: SomeApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  testSlice: testSlice.reducer,
  authSlice: authSlice.reducer,
  appSlice: appSlice.reducer,
  [storageApi.reducerPath]: storageApi.reducer,
  [storageAdminApi.reducerPath]: storageAdminApi.reducer,
  filtersSlice: filtersSlice.reducer,
  [AuthAmplify.reducerPath]: AuthAmplify.reducer,
  [applicationApi.reducerPath]: applicationApi.reducer,
  [visitationApi.reducerPath]: visitationApi.reducer,
  [animalApi.reducerPath]: animalApi.reducer,
  [policyApi.reducerPath]: policyApi.reducer,
  [tenantApi.reducerPath]: tenantApi.reducer,
  [PropertyManagementApi.reducerPath]: PropertyManagementApi.reducer,
  [fileApi.reducerPath]: fileApi.reducer,
  [DocPacketApi.reducerPath]: DocPacketApi.reducer,
  [generalApi.reducerPath]: generalApi.reducer,
  [commonApi.reducerPath]: commonApi.reducer,
  [faqApi.reducerPath]: faqApi.reducer,
  [analyticsApi.reducerPath]: analyticsApi.reducer,
  [curriculumApi.reducerPath]: curriculumApi.reducer,
  [landlordApi.reducerPath]: landlordApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [authPasswordApi.reducerPath]: authPasswordApi.reducer,
  [profileApi.reducerPath]: profileApi.reducer,

  [partnerApiIntegrationAppApi.reducerPath]: partnerApiIntegrationAppApi.reducer,
  // template-flag-1
});

const rootReducer = (state: TState, action: AnyAction) => {
  if (action.type === 'USER_LOGOUT') {
    return appReducer(undefined, action);
  }

  // @ts-ignore
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware().concat(
      userApi.middleware,
      AuthAmplify.middleware,
      fileApi.middleware,
      applicationApi.middleware,
      visitationApi.middleware,
      animalApi.middleware,
      policyApi.middleware,
      tenantApi.middleware,
      PropertyManagementApi.middleware,
      DocPacketApi.middleware,
      storageApi.middleware,
      storageAdminApi.middleware,
      generalApi.middleware,
      commonApi.middleware,
      faqApi.middleware,
      analyticsApi.middleware,
      curriculumApi.middleware,
      landlordApi.middleware,
      authApi.middleware,
      authPasswordApi.middleware,
      profileApi.middleware,
      partnerApiIntegrationAppApi.middleware,
      // template-flag-2
    ),
});

export const clearAllCachedData = () => {
  store.dispatch(userApi.util.resetApiState());
  store.dispatch(AuthAmplify.util.resetApiState());
  store.dispatch(applicationApi.util.resetApiState());
  store.dispatch(visitationApi.util.resetApiState());
  store.dispatch(animalApi.util.resetApiState());
  store.dispatch(policyApi.util.resetApiState());
  store.dispatch(tenantApi.util.resetApiState());
  store.dispatch(DocPacketApi.util.resetApiState());
  store.dispatch(PropertyManagementApi.util.resetApiState());
  store.dispatch(storageApi.util.resetApiState());
  store.dispatch(storageAdminApi.util.resetApiState());
  store.dispatch(generalApi.util.resetApiState());
  store.dispatch(commonApi.util.resetApiState());
  store.dispatch(faqApi.util.resetApiState());
  store.dispatch(analyticsApi.util.resetApiState());
  store.dispatch(curriculumApi.util.resetApiState());
  store.dispatch(landlordApi.util.resetApiState());
  store.dispatch(authApi.util.resetApiState());
  store.dispatch(authPasswordApi.util.resetApiState());
  store.dispatch(profileApi.util.resetApiState());
  store.dispatch(partnerApiIntegrationAppApi.util.resetApiState());

  // template-flag-3
};

export type RootState = ReturnType<typeof rootReducer>;
type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = cb =>
  useSelector(cb, shallowEqual);

setupListeners(store.dispatch);
